import { useQuery } from "@tanstack/react-query";
import React from "react";

import { patientFunctionalLifestyleApi } from "../../../apis";
import { FunctionalLifestyleResponseDto } from "../../../libs/cellera-sdk";
import { HEALTH_METRICS } from "../../../types/functional-lifestyle";
import { PATIENT_SECTIONS } from "./detail/PatientDetail";
import IntakeField from "./IntakeField";
import IntakeHeader from "./IntakeHeader";
import LayoutWrapper from "./LayoutWrapper";

interface FunctionalLifestyleAssessmentProps {
  patientId: string;
}

const FunctionalLifestyleAssessment: React.FC<
  FunctionalLifestyleAssessmentProps
> = ({ patientId }) => {
  const { data, isError, error } = useQuery({
    queryKey: ["functionalLifestyle", patientId],
    queryFn: async () => {
      if (!patientId) return null;
      return patientFunctionalLifestyleApi.getFunctionalLifestyleByPatientId({
        patientId,
      });
    },
    enabled: !!patientId,
  });

  React.useEffect(() => {
    if (isError && error) {
      console.error("FunctionalLifestyle query error:", error);
    }
  }, [isError, error]);

  const displayData: FunctionalLifestyleResponseDto = data ?? {
    patientId,
    energyLevels: 0,
    mentalClarityFocus: 0,
    moodEmotionalWellbeing: 0,
    libidoSexualHealth: 0,
    digestion: 0,
    immuneFunction: 0,
    fitnessLevel: 0,
    skinAppearance: 0,
    hairHealth: 0,
    sleep: 0,
    additionalInformation: "",
  };

  const renderRatingField = (metric: (typeof HEALTH_METRICS)[number]) => {
    if (metric.key === "additionalInformation") {
      return;
    }
    const value = displayData[
      metric.key as keyof FunctionalLifestyleResponseDto
    ] as number;

    return (
      <IntakeField
        key={metric.key}
        label={metric.label}
        value={value}
        disabled={true}
      />
    );
  };

  return (
    <>
      <IntakeHeader title={PATIENT_SECTIONS.functionalLifestyle.label} />

      {/* Rating Fields */}
      <LayoutWrapper itemsPerRow={2} spacing={3}>
        {HEALTH_METRICS.map(renderRatingField)}
      </LayoutWrapper>

      <IntakeField
        label="Additional Information"
        value={displayData.additionalInformation ?? ""}
        disabled={true}
        rows={3}
      />
    </>
  );
};

export default FunctionalLifestyleAssessment;
